import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Box, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Button, Chip, TextField, InputAdornment, 
  CircularProgress, Alert, TablePagination, Dialog, DialogTitle, 
  DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  Search, Visibility, Add, Edit, Delete, Business, 
  LocationOn, Phone, Email, Assignment
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { clientesApi } from '../../services/api';
import { Cliente, ClienteCreate, ClienteUpdate, TIPOS_CLIENTE } from '../../types/expediente';

const ClientesList: React.FC = () => {
  // Estados principales
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estados de búsqueda y paginación
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Estados del modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState<Cliente | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Estados del formulario
  const [formData, setFormData] = useState<ClienteCreate>({
    codigo: '',
    nombre: '',
    tipo: '',
    direccion: '',
    contacto: '',
    telefono: '',
    email: '',
    nif: ''
  });

  const navigate = useNavigate();

  // Cargar clientes
  useEffect(() => {
    loadClientes();
  }, []);

  const loadClientes = async () => {
    try {
      setLoading(true);
      const data = await clientesApi.getClientes(0, 1000); // Cargar todos
      setClientes(data);
    } catch (err) {
      setError('Error cargando clientes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar clientes
  const filteredClientes = clientes.filter(cliente =>
    cliente.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cliente.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cliente.tipo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cliente.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cliente.nif?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Paginación
  const paginatedClientes = filteredClientes.slice(
    page * rowsPerPage, 
    page * rowsPerPage + rowsPerPage
  );

  // Manejar formulario
const handleInputChange = (field: keyof ClienteCreate, value: any) => {
  setFormData((prev: ClienteCreate) => ({
    ...prev,
    [field]: value
  }));
};

  // Abrir modal para crear
  const handleCreate = () => {
    setEditingCliente(null);
    setFormData({
      codigo: '',
      nombre: '',
      tipo: '',
      direccion: '',
      contacto: '',
      telefono: '',
      email: '',
      nif: ''
    });
    setModalOpen(true);
  };

  // Abrir modal para editar
  const handleEdit = (cliente: Cliente) => {
    setEditingCliente(cliente);
    setFormData({
      codigo: cliente.codigo || '',
      nombre: cliente.nombre,
      tipo: cliente.tipo || '',
      direccion: cliente.direccion || '',
      contacto: cliente.contacto || '',
      telefono: cliente.telefono || '',
      email: cliente.email || '',
      nif: cliente.nif || ''
    });
    setModalOpen(true);
  };

  // Guardar cliente
  const handleSave = async () => {
    try {
      setModalLoading(true);
      
      if (editingCliente) {
        // Actualizar
        const updateData: ClienteUpdate = formData;
        await clientesApi.updateCliente(editingCliente.id, updateData);
      } else {
        // Crear
        await clientesApi.createCliente(formData);
      }
      
      await loadClientes();
      setModalOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error guardando cliente');
    } finally {
      setModalLoading(false);
    }
  };

  // Eliminar cliente
  const handleDelete = async (cliente: Cliente) => {
    if (!window.confirm(`¿Seguro que quieres eliminar el cliente ${cliente.nombre}?`)) {
      return;
    }

    try {
      await clientesApi.deleteCliente(cliente.id);
      await loadClientes();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error eliminando cliente');
    }
  };

  // Validar formulario
  const isFormValid = () => {
    return formData.nombre.trim() !== '';
  };

  // Obtener color del chip según tipo
  const getTipoColor = (tipo: string) => {
    switch (tipo?.toLowerCase()) {
      case 'juzgado': return 'primary';
      case 'despacho': return 'secondary';
      case 'particular': return 'success';
      case 'empresa': return 'warning';
      case 'administración': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          🏢 Clientes ({filteredClientes.length})
        </Typography>
        <Box display="flex" gap={1}>
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={handleCreate}
            color="primary"
          >
            Nuevo Cliente
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/')}
          >
            Volver al Dashboard
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Búsqueda */}
      <Box mb={3}>
        <TextField
          fullWidth
          placeholder="Buscar por nombre, código, tipo, email o NIF..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Contacto</TableCell>
              <TableCell>Dirección</TableCell>
              <TableCell>NIF</TableCell>
              <TableCell>Fecha Registro</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedClientes.map((cliente) => (
              <TableRow key={cliente.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Business color="action" />
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {cliente.nombre}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {cliente.codigo && `Código: ${cliente.codigo}`}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={cliente.tipo || 'Sin especificar'} 
                    color={getTipoColor(cliente.tipo || '') as any} 
                    size="small" 
                  />
                </TableCell>
                <TableCell>
                  <Box>
                    {cliente.contacto && (
                      <Typography variant="body2">
                        👤 {cliente.contacto}
                      </Typography>
                    )}
                    {cliente.telefono && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Phone sx={{ fontSize: 14 }} />
                        <Typography variant="body2">{cliente.telefono}</Typography>
                      </Box>
                    )}
                    {cliente.email && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Email sx={{ fontSize: 14 }} />
                        <Typography variant="body2">{cliente.email}</Typography>
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {cliente.direccion && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <LocationOn sx={{ fontSize: 14 }} color="action" />
                      <Typography variant="body2">{cliente.direccion}</Typography>
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {cliente.nif || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {new Date(cliente.created_at).toLocaleDateString('es-ES')}
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5}>
                    <Button 
                      size="small" 
                      startIcon={<Visibility />} 
                      onClick={() => navigate(`/clientes/${cliente.id}`)}
                    >
                      Ver
                    </Button>
                    <Button 
                      size="small" 
                      startIcon={<Edit />} 
                      onClick={() => handleEdit(cliente)}
                      color="primary"
                    >
                      Editar
                    </Button>
                    <Button 
                      size="small" 
                      startIcon={<Delete />} 
                      onClick={() => handleDelete(cliente)}
                      color="error"
                    >
                      Eliminar
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {paginatedClientes.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box py={4}>
                    <Business sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                    <Typography variant="body1" color="textSecondary">
                      No se encontraron clientes
                    </Typography>
                    {searchTerm && (
                      <Typography variant="body2" color="textSecondary">
                        Prueba con otros términos de búsqueda
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        
        {/* Paginación */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredClientes.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Filas por página:"
        />
      </TableContainer>

      {/* Modal Crear/Editar */}
      <Dialog 
        open={modalOpen} 
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Business />
            {editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3} pt={1}>
            {/* Fila 1: Código y Nombre */}
            <Box display="flex" gap={2}>
              <TextField
                label="Código"
                value={formData.codigo}
                onChange={(e) => handleInputChange('codigo', e.target.value)}
                sx={{ flex: 1 }}
                helperText="Código único del cliente"
              />
              <TextField
                label="Nombre *"
                value={formData.nombre}
                onChange={(e) => handleInputChange('nombre', e.target.value)}
                required
                sx={{ flex: 2 }}
              />
            </Box>
            
            {/* Fila 2: Tipo y Contacto */}
            <Box display="flex" gap={2}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={formData.tipo}
                  label="Tipo"
                  onChange={(e) => handleInputChange('tipo', e.target.value)}
                >
                  <MenuItem value="">
                    <em>Sin especificar</em>
                  </MenuItem>
                  {TIPOS_CLIENTE.map(tipo => (
                    <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Persona de Contacto"
                value={formData.contacto}
                onChange={(e) => handleInputChange('contacto', e.target.value)}
                sx={{ flex: 1 }}
              />
            </Box>
            
            {/* Fila 3: Teléfono y Email */}
            <Box display="flex" gap={2}>
              <TextField
                label="Teléfono"
                value={formData.telefono}
                onChange={(e) => handleInputChange('telefono', e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                sx={{ flex: 1 }}
              />
            </Box>
            
            {/* Fila 4: Dirección */}
            <TextField
              label="Dirección"
              value={formData.direccion}
              onChange={(e) => handleInputChange('direccion', e.target.value)}
              multiline
              rows={2}
              fullWidth
            />
            
            {/* Fila 5: NIF */}
            <TextField
              label="NIF/CIF"
              value={formData.nif}
              onChange={(e) => handleInputChange('nif', e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => setModalOpen(false)}
            disabled={modalLoading}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSave}
            variant="contained"
            disabled={!isFormValid() || modalLoading}
            startIcon={modalLoading ? <CircularProgress size={16} /> : undefined}
          >
            {modalLoading ? 'Guardando...' : editingCliente ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientesList;